/**
 * Fair Home V2 — Popular today section (H8)
 * Figma `1:1848` desktop · `1:1602` compact
 */
import type { PopularTodayProduct } from '@/features/recommendations/api/popularToday'
import type { ProductCustomCardItem } from '@/types/ProductType'
import { mapFairSearchProduct } from '@/widgets/search/fair-v2/fair-search-products.data'
import {
  FAIR_PRODUCT_V2_RECS_COMPACT_CARD_WIDTH,
  FAIR_PRODUCT_V2_RECS_DESKTOP_GAP,
  FAIR_PRODUCT_V2_RECS_DESKTOP_PAGE_SIZE,
  getFairProductV2RecsPageCount,
  normalizeFairProductV2RecsPage,
  resolveFairProductV2RecsScrollPage,
  type FairProductV2RecsCard,
} from '@/widgets/product/fair-v2/fair-product-v2.data'
import { mapFairHomeV2CapsulesAudience } from './fair-home-v2-capsules.data'

export const FAIR_HOME_V2_POPULAR_INITIAL_LIMIT = 12

export const FAIR_HOME_V2_POPULAR_LOAD_MORE_STEP = 12

/** T-153 — Popular today mobile `706:47312`: 12 between cards (PDP carousels keep their 24). */
export const FAIR_HOME_V2_POPULAR_COMPACT_GAP = 12

export {
  FAIR_PRODUCT_V2_RECS_COMPACT_CARD_WIDTH as FAIR_HOME_V2_POPULAR_COMPACT_CARD_WIDTH,
  FAIR_PRODUCT_V2_RECS_DESKTOP_GAP as FAIR_HOME_V2_POPULAR_DESKTOP_GAP,
  FAIR_PRODUCT_V2_RECS_DESKTOP_PAGE_SIZE as FAIR_HOME_V2_POPULAR_DESKTOP_PAGE_SIZE,
}

export type FairHomeV2PopularCard = FairProductV2RecsCard

function mapCollectionToPopularCard(
  item: ProductCustomCardItem,
): FairHomeV2PopularCard {
  const mapped = mapFairSearchProduct(item)
  return {
    id: mapped.id,
    variantId: mapped.variantId,
    brand: mapped.brand,
    title: mapped.title,
    price: mapped.price,
    images: mapped.images,
    isFavorite: mapped.isFavorite,
  }
}

export function resolveFairHomeV2PopularCards(
  items: ProductCustomCardItem[] | undefined | null,
): FairHomeV2PopularCard[] {
  return (items ?? []).map(mapCollectionToPopularCard)
}

export function mapFairHomeV2PopularAudience(audience: string): string {
  return mapFairHomeV2CapsulesAudience(audience)
}

export function getFairHomeV2PopularPageCount(
  totalProducts: number,
  compact: boolean,
): number {
  if (compact) return Math.max(1, totalProducts)
  return getFairProductV2RecsPageCount(
    totalProducts,
    FAIR_PRODUCT_V2_RECS_DESKTOP_PAGE_SIZE,
  )
}

export function normalizeFairHomeV2PopularPage(
  page: number,
  pageCount: number,
): number {
  return normalizeFairProductV2RecsPage(page, pageCount)
}

export function resolveFairHomeV2PopularScrollPage(
  scrollLeft: number,
  step: number,
  pageCount: number,
): number {
  return resolveFairProductV2RecsScrollPage(scrollLeft, step, pageCount)
}

/** The endpoint has no page_info; a full page means there may be more. */
export function canFairHomeV2PopularLoadMore(
  productsLength: number,
  limit: number,
): boolean {
  return productsLength >= limit
}

/**
 * Decision 13 — a For You page (`/goods/popular-today?segment=for_you`) as the carousel's cards,
 * through the same card mapping as `/goods/popular` (price «from» = `min_price`).
 */
export function mapFairHomeV2ForYouCards(products: PopularTodayProduct[] | null | undefined): FairHomeV2PopularCard[] {
  return (products ?? []).map((product) =>
    mapCollectionToPopularCard({
      id: product.product_id,
      variant_id: product.product_variant_id,
      name: product.product_name,
      brand: product.brand_name,
      price: { gte: product.min_price ?? 0, lte: product.max_price ?? product.min_price ?? 0 },
      currency: product.currency ?? 'USD',
      discount: null,
      image_urls: product.image_urls?.length ? product.image_urls : product.main_image_url ? [product.main_image_url] : [],
      is_favorite: false,
    }),
  )
}

/** Pages of the For You row, appended in order; a product seen on an earlier page is dropped. */
export function appendFairHomeV2ForYouPage(
  current: FairHomeV2PopularCard[],
  next: FairHomeV2PopularCard[],
): FairHomeV2PopularCard[] {
  const seen = new Set(current.map((card) => card.id))
  return [...current, ...next.filter((card) => !seen.has(card.id))]
}
